import * as React from 'react';
import { motion } from 'framer-motion';

interface EmptyStateProps {
  icon?: React.ReactNode;
  /** Optional eyebrow kicker above the title (e.g. "No invoices · ready to send"). */
  eyebrow?: string;
  title: string;
  description?: string;
  action?: React.ReactNode;
  /** Tone of the icon container (default saffron). */
  tone?: 'saffron' | 'muted';
}

/**
 * Empty state used inside Bento card lists and standalone empty pages.
 * Geist semibold title (no serif on dashboard), saffron-tinted icon
 * container, room for an eyebrow line above the title so context comes
 * through without needing a full page header.
 */
export function EmptyState({ icon, eyebrow, title, description, action, tone = 'saffron' }: EmptyStateProps) {
  const iconClass = tone === 'saffron'
    ? 'bg-saffron/15 ring-saffron/25 text-saffron-foreground'
    : 'bg-muted/60 ring-border/50 text-muted-foreground';
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 120, damping: 22 }}
      className="flex flex-col items-center justify-center text-center py-16 px-8 max-w-md mx-auto"
    >
      {icon && (
        <div className={['mb-5 flex h-14 w-14 items-center justify-center rounded-2xl ring-1 [&_svg]:h-6 [&_svg]:w-6', iconClass].join(' ')}>
          {icon}
        </div>
      )}
      {eyebrow && (
        <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-muted-foreground/80 mb-2">{eyebrow}</p>
      )}
      <h3 className="font-sans font-semibold text-[19px] tracking-[-0.012em] text-foreground text-balance">{title}</h3>
      {description && (
        <p className="text-[13.5px] text-muted-foreground mt-1.5 leading-relaxed">{description}</p>
      )}
      {action && <div className="mt-6">{action}</div>}
    </motion.div>
  );
}
